
import { EventInstance } from './event-instance';
import { DateTime } from 'luxon';
import { PageState } from 'src/app/core/base-page';
import { Page, PageStatus } from '../cbs/page';
import { TimeSeriesData } from '../reporting/time-series-data';
import { DateTimeHelper } from 'src/app/core/helper/datetime.helper';
import { DashListFilter } from '../dash-list-filter';
import { EventTicketType } from './event-ticket-type';

export enum CbsEventType {
    OneTime = 1,
    Recurring = 2,
    Bundled = 3
}
export enum SalesCloseIncrement {
    Minutes = 1,
    Hours = 2,
    Days = 3,
}
export enum RegistrationType {
    RequireRegistration = 1,
    ExpressInterest = 2,
    AnnounceOnly = 3
}

export class CbsEvent extends Page {

    protected static override className = 'Event';
    protected static override idField = 'eventId';

    eventId: number = 0;
    productId: number;
    eventType: CbsEventType;
    shortName: string;
    registrationType: RegistrationType = RegistrationType.RequireRegistration;
    defaultPrice: number;
    adminDropInFee: number = 0;
    reqAcctForRegistration: boolean = false;
    timezone: string;
    daysOfWeek: number;
    weekdayOccurrence: number
    isVirtual: boolean = false;
    instructions: string;
    videoConfUrl: string;
    address: string;
    latitude: string;
    longitude: string;
    useExternal: boolean = false;
    externalEventUrl: string;
    maxAttendees: number;
    passOnFees: boolean = false;
    reqEmergContact: boolean = false;
    allowSelfCheckin: boolean = true;
    startDate: string;
    endDate: string;
    eventInstances: EventInstance[];
    googleMapsUrl: string = '';
    startDateObj: DateTime;
    endDateObj: DateTime;
    eventFromTo: string;
    startDateString: string;
    registered: number; // count
    goingYes: number;
    goingMaybe: number;
    revenue: number; // total
    rating: number;  // rating average
    ratings: number; // how many ratings
    reviews: number; // how many
    eventState: string;
    ratingString: string;
    allowDeferredPayment: boolean = false;

    days: any = [
        { value: 1, description: 'Sun', selected: false },
        { value: 2, description: 'Mon', selected: false },
        { value: 4, description: 'Tue', selected: false },
        { value: 8, description: 'Wed', selected: false },
        { value: 16, description: 'Thu', selected: false },
        { value: 32, description: 'Fri', selected: false },
        { value: 64, description: 'Sat', selected: false },
    ];

    remainingSpots: number; 

    constructor(event?: Partial<CbsEvent>) {
        super();
        Object.assign(this, event);
        super.init();

        // these get passed down for sure on event list fetch, since we'll need to render on 
        // calendar. Event schedule page is loading these directly, not sure if we need to worry about overlap
        if (this.eventInstances != null) { this.eventInstances = this.eventInstances.map(m => new EventInstance(m)); }

        this.ratingString = (this.rating != null ? this.rating : '-') + ' (' + (this.ratings > 0 ? this.ratings : '-') + ')';

        this.setMapUrl();
        this.refreshDates();
        this.checkDaysOfWeek();
    }

    get isPast() { return this.endDateObj != null && this.endDateObj < DateTime.utc() }


    get ticketTypes() {
        return this.subItems as EventTicketType[];
    }

    set ticketTypes(types: EventTicketType[]) {
        this.subItems = types;
    }

    refreshDates() {

        // Log.debug('refreshing dates for event ' + this.title)
        // default the dates to now, if one is null, both will be
        if (this.startDate == null || this.startDate === '0001-01-01T00:00:00') {
            this.startDateObj = DateTime.local().startOf('hour');
            this.endDateObj = DateTime.local().startOf('hour');;
            this.startDate = this.startDateObj.toISO();
            this.endDate = this.endDateObj.toISO();
        } else {
            this.startDateObj = DateTime.fromISO(this.startDate, { zone: 'utc' });
            this.endDateObj = DateTime.fromISO(this.endDate, { zone: 'utc' });

            this.startDateObj = this.startDateObj.toLocal();
            this.endDateObj = this.endDateObj.toLocal();

            // udpate datetime strings to be input compatible, BUT WE NEED TO CONVERT BACK TO UTC BEFORE SAVE!
            this.startDate = this.startDateObj.toISO();
            this.endDate = this.endDateObj.toISO();
        }
        this.startDateString = this.startDateObj.toFormat('MMM dd, h:mm a, yyyy');
        this.eventFromTo = DateTimeHelper.readableDateRange(this.startDateObj, this.endDateObj);

        // this is separate from page visibility state because it actually relies on event dates, no visibility dates
        if (this.endDateObj > DateTime.local() && this.startDateObj < DateTime.local()) {
            this.eventState = 'In Progress';
        }
        else if (this.endDateObj > DateTime.local()) {
            this.eventState = 'Upcoming';
        } else {
            this.eventState = 'Past';
        }
    }

    matchesEventState(selected: string) {
        if (selected == 'all') {
            return true;
        }
        return selected == this.eventState.toLowerCase()
    }

    // load days of week locally from daysOfWeek
    checkDaysOfWeek() {
        for (const btn of this.days) {
            btn.selected = ((this.daysOfWeek & btn.value) === btn.value);
        }
    }

    override hasChanges() {
        return CbsEvent.hasChanges(CbsEvent);
    }
   
    override revert() {
        return CbsEvent.revert(CbsEvent);
    }

    override async save(busyState: PageState = null, controller: string | undefined = this.controller, endpoint: string = ''): Promise<boolean> {

        if (this.startDate != null) { this.startDate = DateTime.fromISO(this.startDate).toUTC().toISO(); }
        else { this.startDate = null }
        if (this.endDate != null) { this.endDate = DateTime.fromISO(this.endDate).toUTC().toISO(); }
        else { this.endDate = null }

        if (await super.save(busyState, controller, endpoint)) {
            this.refreshDates();
            return true; ''
        }
        return false;
    }

    private setMapUrl() {
        const location = (this.latitude != null && this.longitude != null) ? `${this.latitude},${this.longitude}` : this.address;
        this.googleMapsUrl = `https://maps.googleapis.com/maps/api/staticmap?size=400x200&zoom=15&markers=size:mid%7Ccolor:red%7C${location}&key=AIzaSyDBekw4bI6V1n3o-waYamfkT0EHuQMGKFo`;
    }


    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    // keep local cache of last accessed object, both a copy that is never modified unless saved,
    // and a 'working copy' that is edited in screens.
    protected static override _cachedServerObj: CbsEvent; // unmodified until saved
    protected static override _cachedObj: CbsEvent; // working copy
    protected static override _cacheExpiry: number;

    public static expireCache() {
        this._cacheExpiry = 0;
    }

    public static override async load(id: number) {
        return await CbsEvent.getById(CbsEvent, id);
    }
    public static async loadForOverview(id: number) {
        return await CbsEvent.getObject(CbsEvent, null, `overview/${id}`, true);
    }

    public static async fetchEventPages(): Promise<Page[]> {
        return await CbsEvent.fetchArray(CbsEvent);
    }

    public static async fetchFilteredEvents(filter: DashListFilter): Promise<CbsEvent[]> {
        // return await CbsEvent.fetchArray(CbsEvent, null, `getFiltered/${filter}`);
        const resp = await CbsEvent.factory.httpService.post(filter, `events/getFiltered`) as CbsEvent[];
        return resp.map(m => new CbsEvent(m));
    }


    public static async fetchEvents(year: number, month: number): Promise<CbsEvent[]> {
        return await CbsEvent.fetchArray(CbsEvent, null, `forYearMonth/${year}/${month}`);
    }

    public static async fetchActiveFundraisingEvents(): Promise<CbsEvent[]> {
        return await CbsEvent.fetchArray(CbsEvent, null, `activeFundraising`, 'json', true);
    }

    public static async fetchAllActiveEvents(count: number = 50): Promise<CbsEvent[]> {
        return await CbsEvent.fetchArray(CbsEvent, null, `activeAll/${count}`, 'json', true);
    }


    public static async fetchRegistrationsTimeSeries(eventId: number, instanceId: number): Promise<TimeSeriesData[]> {
        const resp = await super.fetchUntyped('Event Registrations', 'cbsreports',
            `registrationsTimeSeries/${eventId}/${instanceId}`, 'json') as TimeSeriesData[];
        return resp;
    }

    public static async fetchEventDashTimeSeries(): Promise<TimeSeriesData[]> {
        const resp = await super.fetchUntyped('Event Dash Report', 'cbsreports',
            `eventDashTimeSeries`, 'json') as TimeSeriesData[];
        return resp;
    }

    public static eventCount: number;
    public static async fetchEventCount() {
        if (CbsEvent.eventCount == null) {
            CbsEvent.eventCount = +(await CbsEvent.fetchUntyped('', 'Events', 'countUpcoming', 'text'));
        }
        return CbsEvent.eventCount;
    }
}
